import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, Minus, Trophy, Timer, Target } from "lucide-react";

export default function QuizResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/quiz/${id}`).then((r) => setResult(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-10 sm:px-6">
        <Skeleton className="h-28" />
        <Skeleton className="h-80" />
      </div>
    );
  }
  if (!result) return <div className="p-10 text-center text-sm text-muted-foreground">Result not found.</div>;

  const pct = result.percentage || 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">Result</div>
        <h1 className="mt-1 font-display text-3xl font-bold">Quiz complete</h1>
        <p className="mt-1 text-sm text-muted-foreground capitalize">{result.mode} mode · {new Date(result.submitted_at).toLocaleString()}</p>
      </div>

      {/* Score summary */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="border-border/70 bg-[hsl(var(--brand-navy))] text-white">
          <CardContent className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-white/70">Score</div>
            <div className="mt-1 font-display text-4xl font-black" data-testid="result-percentage">{pct}%</div>
            <div className="mt-1 text-xs text-white/70">{result.score}/{result.total}</div>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="flex items-center gap-3 p-5">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            <div>
              <div className="text-xs text-muted-foreground">Correct</div>
              <div className="font-display text-2xl font-bold">{result.score}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="flex items-center gap-3 p-5">
            <XCircle className="h-7 w-7 text-destructive" />
            <div>
              <div className="text-xs text-muted-foreground">Incorrect</div>
              <div className="font-display text-2xl font-bold">{result.incorrect}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/70">
          <CardContent className="flex items-center gap-3 p-5">
            <Minus className="h-7 w-7 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">Unanswered</div>
              <div className="font-display text-2xl font-bold">{result.unanswered}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subject breakdown */}
      {result.subject_stats && (
        <Card className="mt-6 border-border/70">
          <CardContent className="p-6">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-[hsl(var(--brand-orange))]" />
              <div className="font-display text-base font-bold">Subject accuracy</div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {Object.entries(result.subject_stats).map(([name, s]) => (
                <div key={name} className="rounded-xl border border-border/70 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="font-semibold">{name}</div>
                    <div className="font-mono text-xs">{s.correct}/{s.total}</div>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full bg-[hsl(var(--brand-orange))]"
                      style={{ width: `${s.total ? (s.correct / s.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question review */}
      <div className="mt-6">
        <h2 className="font-display text-xl font-bold">Review all answers</h2>
        <div className="mt-4 space-y-3">
          {result.answers_detail?.map((d, i) => (
            <Card key={d.question_id} className={`border-l-4 ${d.is_correct ? "border-l-emerald-500" : d.selected ? "border-l-destructive" : "border-l-muted-foreground"}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div>
                    Q{i + 1} · {d.subject_name}{d.topic_name && ` · ${d.topic_name}`} · {d.difficulty}
                  </div>
                  <div className="font-semibold">
                    {d.is_correct ? <span className="text-emerald-600">Correct</span> :
                     d.selected ? <span className="text-destructive">Incorrect</span> :
                     <span>Unanswered</span>}
                  </div>
                </div>
                <div className="mt-2 font-display text-base font-semibold">{d.text}</div>
                <div className="mt-3 grid gap-2">
                  {Object.keys(d.options_shown).sort().map((k) => {
                    const isCorrect = k === d.correct_option;
                    const isSelected = k === d.selected;
                    return (
                      <div
                        key={k}
                        className={`flex items-start gap-3 rounded-lg border px-4 py-2.5 text-sm ${
                          isCorrect ? "border-emerald-500 bg-emerald-50" :
                          isSelected ? "border-destructive bg-destructive/5" : "border-border"
                        }`}
                      >
                        <div className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-xs font-bold ${
                          isCorrect ? "bg-emerald-500 text-white" : isSelected ? "bg-destructive text-white" : "bg-secondary"
                        }`}>{k}</div>
                        <div className="flex-1">{d.options_shown[k]}</div>
                      </div>
                    );
                  })}
                </div>
                {d.explanation && (
                  <div className="mt-3 rounded-lg bg-secondary/60 px-4 py-3 text-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-orange))]">Explanation</div>
                    <div className="mt-1 whitespace-pre-wrap">{d.explanation}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        <Button asChild variant="outline" data-testid="result-dashboard-btn"><Link to="/dashboard">Back to dashboard</Link></Button>
        <Button asChild className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="result-newquiz-btn">
          <Link to="/quiz/new">Start another quiz</Link>
        </Button>
      </div>
    </div>
  );
}
